import EVENTS from "../lib/events.js"
import srraf from "srraf"
import setupMediaLightbox from "../lib/media-lightbox.js"
import setupModelViewer from "../lib/model-viewer.js"
import { liquidMadeVideoPlayer } from "../lib/video-player.js"
import { pausePlayingVideos } from "../lib/video-player-state.js"

class ProductMedia extends HTMLElement {
  constructor() {
    super()

    this.selectors = {
      activeThumb: ".product-thumbnails__item-link.active",
      carouselTabIndex: "[js-update-carousel-tabindex]",
      initialMediaContainer: "[data-initial-slide]",
      iframe: "iframe",
      image: ".image",
      mainMediaContainer: ".product-media__items",
      media: ".media",
      mediaItemWrapper: "[js-media-item-wrapper]",
      mobileLoopButton: ".scroll-slider__mobile-loop-nav",
      thumb: "[js-product-thumbnail]",
      thumbById: (id) => `[data-thumbnail-id='${id}']`,
      mediaById: (id) => `[data-slide-index='${id}']`,
      thumbContainer: "[js-product-thumbnails]",
      thumbGradient: ".product-thumbnails__end-cap-gradient",
      video: "video",
      videoPlayerContainer: "[js-video-player-container]",
      videoPoster: ".video-poster-container",
      viewInYourSpace: "[js-in-your-space]",
    }
  }

  connectedCallback() {
    this.controller = new AbortController()

    this.initActiveMedia()

    // Listen for variant change, handle media depending on media grouping setting
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      (event) => {
        if (event.detail.hasMediaGrouping) {
          this.handleRepaint(event)
        } else {
          this.updateActiveMainMediaToVariant(event)
        }
      },
      { signal: this.controller.signal },
    )

    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_LOAD,
      this.handleLeftThumbnailContainerHeight(),
      {
        signal: this.controller.signal,
      },
    )
  }

  initActiveMedia() {
    this.media = this.querySelector(this.selectors.media)

    if (this.media === null) return

    this.activeMediaController = new AbortController()
    this.hasSingleMedia = this.dataset.hasSingleMedia === "true"

    this.mediaLayout = this.dataset.mediaLayout
    this.mainMediaContainer = this.querySelector(
      this.selectors.mainMediaContainer,
    )
    this.initialMediaContainer = this.querySelector(
      this.selectors.initialMediaContainer,
    )

    // Setup media lightbox
    if (this.dataset.hasProductLightbox === "true") {
      setupMediaLightbox(this.mainMediaContainer)
    }

    // Setup model viewer
    setupModelViewer(this.mainMediaContainer)

    // Load video players
    this.videoPlayerContainers = this.querySelectorAll(
      this.selectors.videoPlayerContainer,
    )
    this.videoPlayerContainers.forEach((video) => liquidMadeVideoPlayer(video))

    //
    // Desktop carousel & all Mobile layouts
    //
    // Load thumbnail indicators
    if (this.mediaLayout === "thumbnails" && this.dataset.thumbnailPosition) {
      this.thumbnailPosition = this.dataset.thumbnailPosition
    }

    if (!this.hasSingleMedia) {
      this.activeThumb = this.querySelector(this.selectors.activeThumb)
      this.activeMediaId = this.activeThumb.dataset.thumbnailId
      this.scrollerId = this.dataset.scrollerId

      // Listen for carousel sliding
      document.addEventListener(
        EVENTS.SCROLL_SLIDER_CHANGED(this.scrollerId),
        (event) => {
          const currentMediaEl = event.detail.currentElement
          const previousMediaEl = event.detail.previousElement
          this.mobileLoopButton = this.querySelector(
            this.selectors.mobileLoopButton,
          )

          if (currentMediaEl) {
            if (currentMediaEl !== this.mobileLoopButton) {
              this.updateActiveThumbnails(currentMediaEl.dataset.mediaItemId)
            }

            if (currentMediaEl.dataset.mediaType === "model") {
              this.initViewInYourSpace(currentMediaEl.dataset.mediaItemId)
            }
          }

          // Pause & Add poster back to any active iframes
          if (this.mediaLayout === "thumbnails") {
            if (currentMediaEl) {
              this.updateCarouselA11yNav(currentMediaEl)
            }

            if (previousMediaEl) {
              const prevMediaType = previousMediaEl.dataset.mediaType

              if (
                prevMediaType === "external_video" ||
                prevMediaType === "video"
              ) {
                const injectedVideoIframe = previousMediaEl.querySelector(
                  this.selectors.iframe,
                )
                const injectedVideoElement = previousMediaEl.querySelector(
                  this.selectors.video,
                )

                if (injectedVideoIframe || injectedVideoElement) {
                  pausePlayingVideos()
                }
              }
            }
          }
        },
        { signal: this.activeMediaController.signal },
      )

      //
      // Desktop carousel layouts only
      //
      if (this.mediaLayout === "thumbnails") {
        this.initThumbnailListeners()

        // Set tabindex within carousels
        this.querySelectorAll(this.selectors.carouselTabIndex).forEach((el) => {
          el.setAttribute("tabindex", "-1")
        })
      }
    }
  }

  handleLeftThumbnailContainerHeight() {
    if (!this.initialMediaContainer) return

    this.videoPoster = this.querySelector(this.selectors.videoPoster)
    const initialMedia = this.initialMediaContainer.querySelector(
      this.selectors.media,
    )
    const mediaEl = this.videoPoster ? this.videoPoster : initialMedia

    // Allow for slight delay to improve container height within T.E.
    setTimeout(() => {
      this.style.setProperty(
        "--left-thumbnail-container-height",
        `${mediaEl.offsetHeight}px`,
      )
    }, 0)
  }

  initThumbnailListeners() {
    // Listen for page loaded to get left thumbnail column height
    if (this.thumbnailPosition === "left") {
      const initialImg = this.initialMediaContainer.querySelector(
        this.selectors.image,
      )

      initialImg
        .decode()
        .then(() => {
          this.handleLeftThumbnailContainerHeight()
        })
        .then(() => {
          initialImg.classList.add("initial-image")
        })
    }

    const smallDesktop = 1023 // 1023 to make sure 1024 is properly styled
    this.widthWatcher = srraf(({ vw, pvw }) => {
      if (vw !== pvw && vw > smallDesktop) {
        this.handleLeftThumbnailContainerHeight()
      }
    })

    // Listen for thumbnail container scroll to apply gradients
    this.thumbContainer = this.querySelector(this.selectors.thumbContainer)
    this.thumbGradient = this.querySelector(this.selectors.thumbGradient)

    this.thumbContainer.addEventListener(
      "scroll",
      () => {
        this.updateThumbGradientOpacity()
      },
      {
        signal: this.activeMediaController.signal,
      },
    )

    // Listen for thumbnail clicks
    this.thumbnailItems = this.querySelectorAll(this.selectors.thumb)
    this.thumbnailItems.forEach((item) => {
      item.addEventListener(
        "click",
        () => {
          this.updateActiveMainMedia(parseInt(item.dataset.thumbnailIndex))
          this.updateActiveThumbnails(item.dataset.thumbnailId)
        },
        { signal: this.activeMediaController.signal },
      )
    })
  }

  initViewInYourSpace(currentActiveMediaId) {
    this.querySelector(this.selectors.viewInYourSpace).setAttribute(
      "data-shopify-model3d-id",
      currentActiveMediaId,
    )
  }

  updateActiveMainMedia(currentThumbnailIndex) {
    const isDesktopViewport =
      (window.innerWidth || document.documentElement.clientWidth) > 1024

    if (isDesktopViewport && this.mediaLayout === "columns") {
      const featuredMediaEl = this.querySelector(
        `${this.selectors.mediaById(currentThumbnailIndex)}`,
      )

      featuredMediaEl.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      })
    }

    // Scroll slider event included for Column layouts for mobile carousel coverage
    document.dispatchEvent(
      new CustomEvent(EVENTS.SCROLL_SLIDER_GO_TO_SLIDE(this.scrollerId), {
        detail: {
          slideIndex: currentThumbnailIndex,
          behavior: "instant",
        },
      }),
    )
  }

  updateActiveMainMediaToVariant(event) {
    const newMediaId = event.detail.newFeaturedMedia
    this.mediaItemWrappers = this.querySelectorAll(
      this.selectors.mediaItemWrapper,
    )

    let newMediaIndex
    for (const media of this.mediaItemWrappers) {
      if (media.dataset.mediaItemId === newMediaId) {
        newMediaIndex = parseInt(media.dataset.slideIndex)
        break
      }
    }

    this.updateActiveMainMedia(newMediaIndex)
  }

  updateActiveThumbnails(currentActiveMediaId) {
    const lastActiveThumbnail = this.querySelector(this.selectors.activeThumb)

    let activeThumbnail = this.querySelector(
      `${this.selectors.thumbById(currentActiveMediaId)}`,
    )

    // Media grouping may create null activeThumbnails
    if (activeThumbnail === null) {
      const firstMediaId = this.querySelector(this.selectors.media).dataset
        .mediaId

      activeThumbnail = this.querySelector(
        `${this.selectors.thumbById(firstMediaId)}`,
      )
    }

    if (activeThumbnail !== lastActiveThumbnail) {
      // Add and remove active thumbnail indicator
      activeThumbnail.classList.add("active")
      lastActiveThumbnail.classList.remove("active")
      this.activeMediaId = currentActiveMediaId

      // Scroll active thumbnail into view
      if (this.mediaLayout === "thumbnails") {
        const scrollAttribute =
          this.thumbnailPosition === "left" ? "scrollTop" : "scrollLeft"

        const scrollDistance =
          this.thumbnailPosition === "left"
            ? activeThumbnail.offsetTop -
              activeThumbnail.getBoundingClientRect().height * 2
            : activeThumbnail.offsetLeft -
              activeThumbnail.getBoundingClientRect().width * 2

        this.thumbContainer[scrollAttribute] = scrollDistance
      }
    }
  }

  updateThumbGradientOpacity() {
    let beforeOpacity = 0
    let afterOpacity = 1

    if (this.thumbnailPosition === "left") {
      if (this.thumbContainer.scrollTop !== 0) {
        beforeOpacity = 1
      }

      const thumbContainerScrollHeight =
        this.thumbContainer.scrollHeight - this.thumbGradient.offsetHeight
      const thumbContainerMaxScroll = this.thumbContainer.scrollTop + 1
      if (thumbContainerMaxScroll >= thumbContainerScrollHeight) {
        afterOpacity = 0
      }
    } else if (this.thumbnailPosition === "below") {
      if (this.thumbContainer.scrollLeft !== 0) {
        beforeOpacity = 1
      }

      const thumbContainerScrollWidth =
        this.thumbContainer.scrollWidth - this.thumbGradient.offsetWidth
      const thumbContainerMaxScroll = this.thumbContainer.scrollLeft + 1

      if (thumbContainerMaxScroll >= thumbContainerScrollWidth) {
        afterOpacity = 0
      }
    }

    this.thumbGradient.style.setProperty(
      "--thumb-opacity-before",
      beforeOpacity,
    )
    this.thumbGradient.style.setProperty("--thumb-opacity-after", afterOpacity)
  }

  updateCarouselA11yNav(currentActiveMediaEl) {
    this.allTabIndexEls = this.querySelectorAll(this.selectors.carouselTabIndex)
    this.currentTabIndexEls = currentActiveMediaEl.querySelectorAll(
      this.selectors.carouselTabIndex,
    )

    this.allTabIndexEls.forEach((el) => {
      el.setAttribute("tabindex", "-1")
    })

    this.currentTabIndexEls.forEach((el) => {
      el.removeAttribute("tabindex")
    })
  }

  handleRepaint(event) {
    // In the case that Media Grouping image order creates conflicts, prevent repaint
    if (event.detail.mediaSize > 0) {
      this.activeMediaController.abort()
      this.controller.abort()
      this.connectedCallback()
      this.initActiveMedia()
    }
  }

  disconnectedCallback() {
    this.controller.abort()
  }
}

if (!customElements.get("product-media")) {
  customElements.define("product-media", ProductMedia)
}
